// src/hr/timeoff/MyTimeOff.js -- the employee's home in Time Off (mockup screen 1 + R1).
// Greeting and next day off, a banner per policy waiting on them, the PTO hero (ring, breakdown,
// one cell per day), compact cards for their other types, their timeline (Requests / Coming up /
// Taken) and who's out this week. Requests, sick reports and balance history open in drawers.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiPlus, FiThermometer, FiEdit3, FiClock, FiCalendar, FiShield, FiArrowRight, FiAlertCircle, FiX
} from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Btn, Card, DayCells, EmptyState, Pill, Ring } from '../kit';
import { daysLabel, daysNumber, shortRangeLabel } from '../format';
import WhosOutCalendar from '../WhosOutCalendar';
import RequestDrawer from './RequestDrawer';
import SickReportDialog from './SickReportDialog';
import BalanceHistory from './BalanceHistory';
import {
  greetingFor, hourNY, longDate, daysBetween, weekdayLong, weekdayShort, shortDate, monthShort,
  dayOfMonth, workdaysBetween
} from './dates';
import { typeLabel, typeTone, REQUEST_STATUS, requestStatusOf, isPending, isCancellable, lengthWords } from './labels';

const TAKEN_PREVIEW = 4;
const byStart = (a, b) => a.start.localeCompare(b.start) || a.end.localeCompare(b.end);

// ---------------------------------------------------------------------------------------------
// Header pieces
// ---------------------------------------------------------------------------------------------

function NextDayOff({ date, today }) {
  const n = date ? daysBetween(today, date) : null;
  let when = 'Nothing booked yet';
  if (date && n <= 0) when = 'Today';
  else if (date && n === 1) when = 'Tomorrow';
  else if (date) when = `${weekdayLong(date)} · in ${n} days`;
  return (
    <div className="hrp-next">
      <div className="hrp-next__cal" aria-hidden="true">
        <b>{date ? monthShort(date).toUpperCase() : 'NEXT'}</b>
        <span>{date ? dayOfMonth(date) : <FiCalendar />}</span>
      </div>
      <div className="hrp-next__txt">
        <div className="hrp-next__t1">Your next day off</div>
        <div className="hrp-next__t2">{when}</div>
        {date && <span className="hrp-sr">{longDate(date)}</span>}
      </div>
    </div>
  );
}

function PolicyBanner({ policy, today, onOpen }) {
  const overdue = policy.dueDate && policy.dueDate < today;
  let text = 'Open it, read it, then confirm with one click.';
  if (overdue) text = `Overdue — it was due ${weekdayShort(policy.dueDate)}, ${shortDate(policy.dueDate)}`;
  else if (policy.dueDate) text = `Please read it by ${weekdayShort(policy.dueDate)}, ${shortDate(policy.dueDate)}`;
  return (
    <div className="hrp-banner" data-overdue={overdue ? 'true' : undefined}>
      <span className="hrp-banner__ic" aria-hidden="true"><FiEdit3 /></span>
      <div className="hrp-banner__txt">
        <div className="hrp-banner__title">{`${policy.title} v${policy.version} needs your acknowledgment`}</div>
        <div className="hrp-banner__text">{text}</div>
      </div>
      <Btn size="sm" onClick={onOpen}>Review &amp; sign</Btn>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Balances
// ---------------------------------------------------------------------------------------------

function heroBalanceOf(balances) {
  return balances.find((b) => b.key === 'pto' && b.setUp)
    || balances.find((b) => !b.isSick && b.setUp)
    || null;
}

function HistoryLink({ onClick }) {
  return (
    <button type="button" className="hrp-link hrp-hero__hist" onClick={onClick}>
      <FiClock aria-hidden="true" /> Balance history
    </button>
  );
}

function PtoHero({ balance, year, onHistory }) {
  const total = balance.totalHours;
  const available = balance.availableHours;
  const share = total > 0 ? Math.max(0, Math.min(1, available / total)) : 0;
  const pct = Math.round(share * 100);
  const name = balance.key === 'pto' ? 'Paid time off' : typeLabel(balance);
  const booked = balance.scheduledHours + balance.pendingHours;
  const facts = [
    ['This year', daysNumber(balance.allowanceHours)],
    ['Carried over', balance.carriedOverHours > 0 ? `+${daysNumber(balance.carriedOverHours)}` : daysNumber(balance.carriedOverHours)],
    ['Used', daysNumber(balance.usedHours)],
    ['Scheduled', daysNumber(balance.scheduledHours)],
    ['Pending', daysNumber(balance.pendingHours)]
  ];
  return (
    <Card feat delay={0} className="hrp-hero">
      <div className="hrp-hero__top">
        <Ring
          value={Math.max(0, available)}
          max={total}
          size={118}
          stroke={10}
          label={`${pct}%`}
          sub="available"
          ariaLabel={`${daysLabel(available)} of ${daysLabel(total)} left to book`}
        />
        <div className="hrp-hero__txt">
          <div className="hrp-hero__labrow">
            <h2 className="hrp-lab">{name}</h2>
            <HistoryLink onClick={onHistory} />
          </div>
          <div className="hrp-hero__big" data-neg={available < 0 ? 'true' : undefined}>
            {daysNumber(available)}
            <small>{Math.abs(available) === 8 ? 'day left to book' : 'days left to book'}</small>
          </div>
          <ul className="hrp-brk" aria-label={`${typeLabel(balance)} breakdown`}>
            {facts.map(([label, value]) => (
              <li key={label}><b>{value}</b><span>{label}</span></li>
            ))}
          </ul>
          {balance.usedBeforePortalHours > 0 && (
            <p className="hrp-hero__hint">{`Used includes ${daysLabel(balance.usedBeforePortalHours)} from before the portal (${year}).`}</p>
          )}
        </div>
      </div>
      <DayCells
        totalHours={total}
        usedHours={balance.usedHours}
        scheduledHours={booked}
        legend={false}
        ariaLabel={`${daysLabel(Math.max(0, available))} of ${daysLabel(total)} left to book, ${daysNumber(booked)} scheduled or pending, ${daysNumber(balance.usedHours)} used`}
      />
      <ul className="hrp-legend" aria-hidden="true">
        <li><i className="hrp-legend__key hrp-legend__key--available" />Available</li>
        <li><i className="hrp-legend__key hrp-legend__key--scheduled" />Scheduled or pending</li>
        <li><i className="hrp-legend__key hrp-legend__key--used" />Used</li>
      </ul>
    </Card>
  );
}

function NotSetUpHero({ year, onHistory }) {
  return (
    <Card feat delay={0} className="hrp-hero hrp-hero--empty">
      <EmptyState compact icon={FiCalendar} title={`Your ${year} balances haven't been set up yet`}>
        HR is adding them. You can still report a sick day.
      </EmptyState>
      <div className="hrp-hero__foot"><HistoryLink onClick={onHistory} /></div>
    </Card>
  );
}

function MiniBalance({ balance, delay }) {
  const total = balance.totalHours;
  const available = balance.availableHours;
  const meta = [`${daysNumber(balance.usedHours)} used`];
  if (balance.scheduledHours > 0) meta.push(`${daysNumber(balance.scheduledHours)} scheduled`);
  if (balance.pendingHours > 0) meta.push(`${daysNumber(balance.pendingHours)} pending`);
  return (
    <Card delay={delay} className="hrp-mini">
      <Ring
        value={Math.max(0, available)}
        max={total}
        size={58}
        stroke={6}
        color={balance.color || '#818cf8'}
        ariaLabel={`${daysLabel(available)} of ${daysLabel(total)} left`}
      />
      <div className="hrp-mini__txt">
        <h2 className="hrp-lab">{typeLabel(balance)}</h2>
        <div className="hrp-mini__num" data-neg={available < 0 ? 'true' : undefined}>
          {daysNumber(available)}<small>{`of ${daysLabel(total)} left`}</small>
        </div>
        <div className="hrp-mini__meta">{meta.join(' · ')}</div>
        {balance.isSick && (
          <p className="hrp-mini__note">Own or family illness, safe time, school meetings, closures.</p>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------------------------
// Timeline
// ---------------------------------------------------------------------------------------------

function describeRequestLength(request, holidays) {
  const days = workdaysBetween(request.startDate, request.endDate, holidays).length;
  const perDay = days > 0 ? request.totalHours / days : 8;
  return lengthWords(perDay, request.totalHours);
}

// Rows for the three groups. Approved requests show through their booked entries (matched on type
// and dates) so they appear once; that link is what makes a future one cancellable.
function buildTimeline(me, today) {
  const typesById = new Map((me.types || []).map((t) => [t.id, t]));
  const holidays = new Set((me.holidays || []).map((h) => h.date));
  const requests = me.requests || [];
  const approved = requests.filter((r) => r.status === 'approved');

  const requestRows = requests
    .filter((r) => isPending(r) || (r.status === 'declined' && r.endDate >= today))
    .map((r) => {
      const type = typesById.get(r.typeId) || { name: r.typeName };
      return {
        key: `r-${r.id}`,
        start: r.startDate,
        end: r.endDate,
        kind: typeLabel(type),
        tone: typeTone(type),
        desc: describeRequestLength(r, holidays),
        status: requestStatusOf(r),
        statusTitle: r.status === 'pending_manager' && r.managerName ? `Waiting on ${r.managerName}` : undefined,
        reason: r.status === 'declined' ? r.declineReason : null,
        request: isCancellable(r, today) ? r : null
      };
    })
    .sort(byStart);

  const entryRow = (e) => {
    const known = typesById.get(e.typeId);
    const type = { key: e.typeKey, name: e.typeName, color: e.color, isSick: !!(known && known.isSick) || e.typeKey === 'sick' };
    const request = e.source === 'request'
      ? approved.find((r) => r.typeId === e.typeId && r.startDate === e.startDate && r.endDate === e.endDate)
      : null;
    // Protected leave is the person's own record: named with its type, never with a note.
    // Sick time never shows a note either.
    let desc = null;
    if (!e.protected) desc = type.isSick || !e.note ? lengthWords(e.hoursPerDay, e.totalHours) : e.note;
    return {
      key: `e-${e.id}`,
      start: e.startDate,
      end: e.endDate,
      protected: !!e.protected,
      kind: e.protected ? `Protected leave · ${e.typeName || 'Leave'}` : typeLabel(type),
      tone: e.protected ? { tone: 'mute' } : typeTone(type),
      desc,
      length: daysLabel(e.totalHours),
      request: request && isCancellable(request, today) ? request : null,
      fromRequest: !!request
    };
  };

  const entries = me.entries || [];
  const upcoming = entries.filter((e) => e.endDate >= today).map(entryRow).sort(byStart).map((row) => {
    let status = { label: row.fromRequest ? 'Approved' : 'Scheduled', tone: 'ok' };
    if (row.start <= today) status = { label: 'Today', tone: 'ok' };
    return { ...row, status };
  });
  const taken = entries.filter((e) => e.endDate < today).map(entryRow).sort((a, b) => byStart(b, a));
  return { requestRows, upcoming, taken };
}

function TimelineRow({ row, onCancel }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const cancelRef = useRef(null);
  const keepRef = useRef(null);
  const wasConfirming = useRef(false);
  const dates = shortRangeLabel(row.start, row.end);

  useEffect(() => {
    if (confirming && keepRef.current) keepRef.current.focus();
    else if (!confirming && wasConfirming.current && cancelRef.current) cancelRef.current.focus();
    wasConfirming.current = confirming;
  }, [confirming]);

  const confirm = async () => {
    setBusy(true);
    setError('');
    try {
      await onCancel(row.request);
      setDone(true);
    } catch (err) {
      setError(errorText(err, "Couldn't cancel this request. Try again."));
    }
    setBusy(false);
    setConfirming(false);
  };

  const status = done ? REQUEST_STATUS.cancelled : row.status;
  const canCancel = !!row.request && !done;

  return (
    <li className="hrp-tl__it" data-protected={row.protected ? 'true' : undefined}>
      <span className="hrp-tl__dt">{dates}</span>
      <span className="hrp-tl__what">
        <Pill {...row.tone} dot={!row.protected} icon={row.protected ? <FiShield /> : undefined} className="hrp-tl__pill">
          {row.kind}
        </Pill>
        {row.desc && <span className="hrp-tl__desc">{row.desc}</span>}
      </span>
      <span className="hrp-tl__end">
        {status
          ? <Pill tone={status.tone} title={row.statusTitle}>{status.label}</Pill>
          : <span className="hrp-tl__len">{row.length}</span>}
        {canCancel && !confirming && (
          <button ref={cancelRef} type="button" className="hrp-link hrp-tl__act" aria-label={`Cancel request for ${dates}`} onClick={() => setConfirming(true)}>
            <FiX aria-hidden="true" />Cancel
          </button>
        )}
      </span>
      {confirming && (
        <div className="hrp-tl__confirm" role="group" aria-label={`Cancel request for ${dates}`}>
          <span>{row.request.status === 'approved' ? 'Cancel this time off? The days go back to your balance.' : 'Withdraw this request?'}</span>
          <span className="hrp-tl__btns">
            <Btn ref={keepRef} variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={busy}>Keep it</Btn>
            <Btn variant="danger" size="sm" loading={busy} onClick={confirm}>Yes, cancel it</Btn>
          </span>
        </div>
      )}
      {row.reason && <p className="hrp-tl__why">{`Reason: “${row.reason}”`}</p>}
      {error && <p className="hrp-tl__err" role="alert"><FiAlertCircle aria-hidden="true" />{error}</p>}
    </li>
  );
}

function TimelineGroup({ title, rows, onCancel }) {
  if (rows.length === 0) return null;
  return (
    <section className="hrp-tl__grp" aria-label={title}>
      <h3 className="hrp-group">{title}</h3>
      <ul className="hrp-tl__list">
        {rows.map((row) => <TimelineRow key={row.key} row={row} onCancel={onCancel} />)}
      </ul>
    </section>
  );
}

function Timeline({ me, today, onCancel, onRequest }) {
  const [showAll, setShowAll] = useState(false);
  const { requestRows, upcoming, taken } = useMemo(() => buildTimeline(me, today), [me, today]);
  const empty = requestRows.length + upcoming.length + taken.length === 0;
  const more = taken.length > TAKEN_PREVIEW;
  const action = more ? (
    <button type="button" className="hrp-link" aria-expanded={showAll} onClick={() => setShowAll((v) => !v)}>
      {showAll ? 'Show less' : `All of ${me.year || ''}`.trim()} <FiArrowRight aria-hidden="true" />
    </button>
  ) : null;

  return (
    <Card delay={3} title="Your time off" action={action} className="hrp-tl">
      {empty ? (
        <EmptyState compact icon={FiCalendar} title="No time off booked yet" action={<Btn size="sm" icon={FiPlus} onClick={onRequest}>Request time off</Btn>}>
          {`Your requests and days off for ${me.year || 'this year'} show up here.`}
        </EmptyState>
      ) : (
        <>
          <TimelineGroup title="Requests" rows={requestRows} onCancel={onCancel} />
          <TimelineGroup title="Coming up" rows={upcoming} onCancel={onCancel} />
          <TimelineGroup title="Taken" rows={showAll ? taken : taken.slice(0, TAKEN_PREVIEW)} onCancel={onCancel} />
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------------------------

export function MyTimeOff({ me, today, reload, onToast }) {
  const navigate = useNavigate();
  const [drawer, setDrawer] = useState(null); // 'request' | 'sick' | 'history'
  const balances = me.balances || [];
  const hero = me.balancesSetUp ? heroBalanceOf(balances) : null;
  const others = balances.filter((b) => b !== hero && (b.isSick || (me.balancesSetUp && b.setUp)));
  const close = () => setDrawer(null);
  const finished = (text) => {
    setDrawer(null);
    onToast(text);
    reload();
  };
  const cancelRequest = async (request) => {
    await hr.cancelRequest(request.id);
    onToast('Request cancelled');
    reload();
  };

  return (
    <div className="hrp-page hrp-mto">
      <div className="hrp-page-head hrp-to-head">
        <div className="hrp-to-head__hi">
          <h1 className="hrp-h1">{`${greetingFor(hourNY())}, ${me.employee.firstName}`}</h1>
          <p className="hrp-sub">{`${longDate(today)} · Here's where your time off stands`}</p>
        </div>
        <div className="hrp-to-head__side">
          <NextDayOff date={me.nextDayOff} today={today} />
          <div className="hrp-to-head__acts">
            <Btn variant="ghost" icon={FiThermometer} onClick={() => setDrawer('sick')}>Out sick today?</Btn>
            <Btn icon={FiPlus} onClick={() => setDrawer('request')}>Request time off</Btn>
          </div>
        </div>
      </div>

      {(me.pendingPolicies || []).length > 0 && (
        <div className="hrp-banners">
          {me.pendingPolicies.map((p) => (
            <PolicyBanner key={p.id} policy={p} today={today} onOpen={() => navigate('policies')} />
          ))}
        </div>
      )}

      <div className={others.length ? 'hrp-to-top' : 'hrp-to-top hrp-to-top--solo'}>
        {hero
          ? <PtoHero balance={hero} year={me.year} onHistory={() => setDrawer('history')} />
          : <NotSetUpHero year={me.year} onHistory={() => setDrawer('history')} />}
        {others.length > 0 && (
          <div className="hrp-side">
            {others.map((b, i) => <MiniBalance key={b.typeId} balance={b} delay={i + 1} />)}
          </div>
        )}
      </div>

      <div className="hrp-to-low">
        <Timeline me={me} today={today} onCancel={cancelRequest} onRequest={() => setDrawer('request')} />
        <Card
          delay={4}
          title="Who's out this week"
          action={<Link to="whos-out">Calendar <FiArrowRight aria-hidden="true" /></Link>}
          className="hrp-wocard"
        >
          <WhosOutCalendar mode="week" today={today} />
        </Card>
      </div>

      <RequestDrawer
        open={drawer === 'request'}
        me={me}
        today={today}
        onClose={close}
        onDone={finished}
        onReportSick={() => setDrawer('sick')}
      />
      <SickReportDialog open={drawer === 'sick'} me={me} today={today} onClose={close} onDone={finished} />
      <BalanceHistory open={drawer === 'history'} me={me} onClose={close} />
    </div>
  );
}

export default MyTimeOff;
